<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useWebOrderStore } from 'dashboard/stores/webOrder';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { WEB_ORDER_ACCESS_PERMISSIONS } from 'dashboard/constants/permissions';
import AgentsAPI from 'dashboard/api/agents';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import AgentProfileCover from 'dashboard/components-next/agent/AgentProfileCover.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const vuexStore = useStore();
const webOrderStore = useWebOrderStore();
const { checkPermissions, shouldShow } = usePolicy();
const currentUser = useMapGetter('getCurrentUser');
const currentUserId = useMapGetter('getCurrentUserID');
const currentRole = useMapGetter('getCurrentRole');
const getAgentById = useMapGetter('agents/getAgentById');

const previewUrl = ref('');
const conversationMetrics = ref({ open: 0, unattended: 0 });

const isMember = computed(() => Boolean(route.params.userId));
const memberId = computed(() => Number(route.params.userId));

const canSeeWebOrder = computed(() =>
  checkPermissions([
    'administrator',
    ...WEB_ORDER_ACCESS_PERMISSIONS,
    'integration_manage',
  ])
);

const canSeePermissions = computed(() =>
  shouldShow(FEATURE_FLAGS.AGENT_MANAGEMENT, ['administrator'])
);

const canSeeOwnReports = computed(() =>
  shouldShow(FEATURE_FLAGS.REPORTS, [
    'administrator',
    'report_manage',
    'report_own',
  ])
);

const canSeeMemberReports = computed(() =>
  shouldShow(FEATURE_FLAGS.REPORTS, [
    'administrator',
    'report_manage',
    'report_agents',
  ])
);

const memberRecord = computed(() => {
  const fromVuex = getAgentById.value(memberId.value) || {};
  if (fromVuex.id) return fromVuex;
  return (
    webOrderStore.agents.find(item => Number(item.id) === memberId.value) || {}
  );
});

const identity = computed(() => {
  if (!isMember.value) {
    const user = currentUser.value || {};
    return {
      name: user.name || '',
      displayName: user.display_name || '',
      email: user.email || '',
      avatar: previewUrl.value || user.avatar_url || '',
      role: currentRole.value || '',
    };
  }
  const agent = memberRecord.value;
  return {
    name: agent.name || '',
    displayName: agent.display_name || agent.displayName || '',
    email: agent.email || '',
    avatar: agent.thumbnail || agent.avatarUrl || agent.avatar_url || '',
    role: agent.role || '',
    customRoleId: agent.custom_role_id || agent.customRoleId || null,
  };
});

const myHubNameForSelf = () => {
  if (route.name === 'profile_member_web_order') return 'profile_web_order';
  if (route.name === 'profile_member_permissions') {
    return 'profile_permissions';
  }
  if (route.name === 'profile_member_reports') return 'profile_reports';
  return 'profile_settings_index';
};

const redirectIfViewingSelf = () => {
  if (!isMember.value) return;
  const selfId = currentUserId.value;
  if (!selfId || String(route.params.userId) !== String(selfId)) return;
  router.replace({
    name: myHubNameForSelf(),
    params: { accountId: route.params.accountId },
  });
};

const roleLabel = computed(() => {
  const role = identity.value.role;
  if (identity.value.customRoleId || role === 'custom_role') {
    return t('AGENT_HUB.ROLE_CUSTOM');
  }
  if (role === 'administrator') {
    return t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR');
  }
  if (role === 'manager') return t('AGENT_MGMT.AGENT_TYPES.MANAGER');
  if (role === 'agent') return t('AGENT_MGMT.AGENT_TYPES.AGENT');
  return '';
});

const workloadId = computed(() =>
  isMember.value ? memberId.value : Number(currentUser.value?.id)
);

const workload = computed(() => {
  const id = workloadId.value;
  return (
    webOrderStore.assignmentCounts?.[id] ||
    webOrderStore.assignmentCounts?.[String(id)] ||
    {}
  );
});

const pendingCount = computed(() => Number(workload.value.pending) || 0);
const onHoldCount = computed(() => {
  const counts = workload.value;
  return Number(counts.onHold ?? counts.on_hold) || 0;
});

const assignedConversationCount = computed(
  () => Number(conversationMetrics.value.open) || 0
);
const unattendedConversationCount = computed(
  () => Number(conversationMetrics.value.unattended) || 0
);

const accountParams = computed(() => ({
  accountId: route.params.accountId,
}));

const tabs = computed(() => {
  if (isMember.value) {
    const params = {
      ...accountParams.value,
      userId: route.params.userId,
    };
    return [
      {
        key: 'overview',
        label: t('AGENT_HUB.TABS.OVERVIEW'),
        to: { name: 'profile_member_overview', params },
        activeNames: ['profile_member_overview'],
      },
      ...(canSeeWebOrder.value
        ? [
            {
              key: 'web-order',
              label: t('AGENT_HUB.TABS.WEB_ORDER'),
              to: { name: 'profile_member_web_order', params },
              activeNames: ['profile_member_web_order'],
            },
          ]
        : []),
      ...(canSeePermissions.value
        ? [
            {
              key: 'permissions',
              label: t('AGENT_HUB.TABS.PERMISSIONS'),
              to: { name: 'profile_member_permissions', params },
              activeNames: ['profile_member_permissions'],
            },
          ]
        : []),
      ...(canSeeMemberReports.value
        ? [
            {
              key: 'reports',
              label: t('AGENT_HUB.TABS.REPORTS'),
              to: { name: 'profile_member_reports', params },
              activeNames: ['profile_member_reports'],
            },
          ]
        : []),
    ];
  }

  const params = accountParams.value;
  return [
    {
      key: 'general',
      label: t('AGENT_HUB.TABS.GENERAL'),
      to: { name: 'profile_settings_index', params },
      activeNames: ['profile_settings_index'],
    },
    {
      key: 'security',
      label: t('AGENT_HUB.TABS.SECURITY'),
      to: { name: 'profile_settings_security', params },
      activeNames: ['profile_settings_security', 'profile_settings_mfa'],
    },
    {
      key: 'notifications',
      label: t('AGENT_HUB.TABS.NOTIFICATIONS'),
      to: { name: 'profile_settings_notifications', params },
      activeNames: ['profile_settings_notifications'],
    },
    ...(canSeeWebOrder.value
      ? [
          {
            key: 'web-order',
            label: t('AGENT_HUB.TABS.WEB_ORDER'),
            to: { name: 'profile_web_order', params },
            activeNames: ['profile_web_order', 'profile_agent_integration'],
          },
        ]
      : []),
    ...(canSeePermissions.value
      ? [
          {
            key: 'permissions',
            label: t('AGENT_HUB.TABS.PERMISSIONS'),
            to: { name: 'profile_permissions', params },
            activeNames: ['profile_permissions'],
          },
        ]
      : []),
    ...(canSeeOwnReports.value
      ? [
          {
            key: 'reports',
            label: t('AGENT_HUB.TABS.REPORTS'),
            to: { name: 'profile_reports', params },
            activeNames: ['profile_reports'],
          },
        ]
      : []),
  ];
});

const activeTabIndex = computed(() => {
  const index = tabs.value.findIndex(tab =>
    tab.activeNames.includes(route.name)
  );
  return index === -1 ? 0 : index;
});

const isTabActive = tab => tab.activeNames.includes(route.name);

const onTabChanged = tab => {
  if (!tab?.to || isTabActive(tab)) return;
  router.push(tab.to);
};

const loadConversationMetrics = async () => {
  const id = workloadId.value;
  if (!id) {
    conversationMetrics.value = { open: 0, unattended: 0 };
    return;
  }

  try {
    const { data } = await AgentsAPI.conversationMetrics(id);
    conversationMetrics.value = {
      open: Number(data?.open) || 0,
      unattended: Number(data?.unattended) || 0,
    };
  } catch {
    conversationMetrics.value = { open: 0, unattended: 0 };
  }
};

const loadIdentity = () => {
  if (canSeeWebOrder.value) {
    webOrderStore.fetchAssignments();
  }
  if (
    canSeePermissions.value ||
    canSeeOwnReports.value ||
    canSeeMemberReports.value
  ) {
    vuexStore.dispatch('agents/get').catch(() => {});
  }
};

onMounted(loadIdentity);
watch(() => route.params.userId, loadIdentity);
watch(workloadId, loadConversationMetrics, { immediate: true });
watch([() => route.fullPath, currentUserId], redirectIfViewingSelf, {
  immediate: true,
});

const saveAvatar = async file => {
  const user = currentUser.value || {};
  try {
    await vuexStore.dispatch('updateProfile', {
      name: user.name,
      email: user.email,
      displayName: user.display_name,
      avatar: file,
    });
    previewUrl.value = '';
    useAlert(t('PROFILE_SETTINGS.UPDATE_SUCCESS'));
  } catch (error) {
    previewUrl.value = '';
    useAlert(
      parseAPIErrorResponse(error) || t('RESET_PASSWORD.API.ERROR_MESSAGE')
    );
  }
};

const onAvatarUpload = ({ file, url }) => {
  if (!file || isMember.value) return;
  previewUrl.value = url || '';
  saveAvatar(file);
};

const onAvatarDelete = async () => {
  if (isMember.value) return;
  try {
    await vuexStore.dispatch('deleteAvatar');
    previewUrl.value = '';
    useAlert(t('PROFILE_SETTINGS.AVATAR_DELETE_SUCCESS'));
  } catch {
    useAlert(t('PROFILE_SETTINGS.AVATAR_DELETE_FAILED'));
  }
};
</script>

<template>
  <div class="flex h-full min-h-0 flex-col">
    <section
      class="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl bg-n-solid-2 outline outline-1 outline-n-container"
    >
      <AgentProfileCover height-class="h-36" rounded-class="rounded-t-2xl" />

      <div class="relative z-10 flex shrink-0 items-end gap-4 px-4 sm:px-6">
        <div class="-mt-10 shrink-0">
          <Avatar
            :src="identity.avatar"
            :name="identity.name || identity.email || ' '"
            :size="80"
            rounded-full
            class="rounded-full ring-4 ring-n-solid-2"
            :allow-upload="!isMember"
            @upload="onAvatarUpload"
            @delete="onAvatarDelete"
          />
        </div>
        <div class="min-w-0 pb-4">
          <h1 class="truncate text-lg font-semibold text-n-slate-12">
            {{ identity.name || t('AGENT_HUB.UNKNOWN_NAME') }}
          </h1>
          <p
            v-if="identity.displayName"
            class="truncate text-sm text-n-slate-11"
          >
            {{ identity.displayName }}
          </p>
          <p class="truncate text-sm text-n-slate-11">
            {{ identity.email }}
          </p>
          <div class="mt-2 flex flex-wrap items-center gap-2">
            <span
              v-if="roleLabel"
              class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs font-medium text-n-slate-12"
            >
              {{ roleLabel }}
            </span>
            <span
              v-tooltip.top="t('AGENT_HUB.ASSIGNED_CONVERSATIONS_HINT')"
              class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-11"
            >
              {{ t('AGENT_HUB.ASSIGNED_CONVERSATIONS') }}
              {{ assignedConversationCount }}
            </span>
            <span
              v-tooltip.top="t('AGENT_HUB.UNATTENDED_CONVERSATIONS_HINT')"
              class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-11"
            >
              {{ t('AGENT_HUB.UNATTENDED_CONVERSATIONS') }}
              {{ unattendedConversationCount }}
            </span>
            <template v-if="canSeeWebOrder">
              <span
                class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-11"
              >
                {{ t('WEB_ORDER.AGENTS.PENDING') }} {{ pendingCount }}
              </span>
              <span
                class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-11"
              >
                {{ t('WEB_ORDER.AGENTS.ON_HOLD') }} {{ onHoldCount }}
              </span>
            </template>
          </div>
        </div>
      </div>

      <div class="shrink-0 overflow-x-auto px-4 pb-3 md:hidden">
        <TabBar
          :tabs="tabs"
          :initial-active-tab="activeTabIndex"
          @tab-changed="onTabChanged"
        />
      </div>

      <div class="flex min-h-0 flex-1 overflow-hidden border-t border-n-weak">
        <nav
          class="hidden w-52 shrink-0 flex-col gap-1 border-r border-n-weak p-3 md:flex"
          :aria-label="t('AGENT_HUB.NAV_LABEL')"
        >
          <router-link
            v-for="tab in tabs"
            :key="tab.key"
            :to="tab.to"
            class="flex h-8 items-center rounded-lg px-2 text-sm text-n-slate-11 hover:bg-n-alpha-2"
            :class="{
              'bg-n-alpha-2 text-n-slate-12': isTabActive(tab),
            }"
          >
            <span class="truncate">{{ tab.label }}</span>
          </router-link>
        </nav>
        <div class="min-w-0 flex-1 overflow-y-auto p-4 md:p-6">
          <router-view />
        </div>
      </div>
    </section>
  </div>
</template>
